import { useRef, useState } from 'react';
import styled from 'styled-components';

import { IconButton } from '../../cdl/IconButton/IconButton';
import { buttonShadows } from '../../cdl/Button/components/buttonShadows';
import { CharacterCount } from '../../common/CharacterCount/CharacterCount';
import { translate } from '../../common/helpers/translate.helper';
import { IconArrowUp } from '../../common/icons/cdl/ArrowUp';
import { theme } from '../../common/ui/theme';
import { Box } from '../../common/ui/Box';
import { useCharacterLimit } from '../ApprovalRequest/useCharacterLimit';

import { GrowingTextArea } from './GrowingTextArea';
import { useMessageCreate } from './useMessageCreate';

const maxLength = 500;

const SendButtonWrapper = styled.div`
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    min-height: 34px;
    padding: 8px;
`;

const ChatSendButton = styled(IconButton)`
    &:not(:disabled) {
        color: ${theme.colors.foreground.onEmphasis};
        background: linear-gradient(0deg, ${theme.colors.accent.emphasis}, ${theme.colors.accent.emphasis}, #3386ff);
        background-size: 200% 200%;
        background-position: 0% 0%;
        box-shadow: ${buttonShadows.emphDrop}, ${buttonShadows.emphBorder};
        transition:
            background-position 75ms ease-out,
            box-shadow 75ms ease-out;
    }

    &:hover:not(:disabled) {
        color: ${theme.colors.foreground.onEmphasis};
        background-position: 100% 100%;
        box-shadow: ${buttonShadows.emphDrop}, ${buttonShadows.emphBorderHover}, ${buttonShadows.dropHover};
    }

    &:focus:not(:disabled) {
        box-shadow: ${theme.shadows.focus}, ${buttonShadows.emphDrop}, ${buttonShadows.emphBorderHover},
            ${buttonShadows.dropHover};
    }

    &:active:not(:disabled) {
        color: ${theme.colors.foreground.onEmphasis};
        background: ${theme.colors.accent.foreground};
        box-shadow: ${theme.shadows.focus}, ${buttonShadows.emphDrop}, ${buttonShadows.emphBorderHover},
            ${buttonShadows.dropHover};
    }

    &:disabled {
        color: ${theme.colors.foreground.disabled};
        background: ${theme.colors.background.subtle};
        box-shadow: none;
    }
`;

export const OfferChatInput = ({ offerId }) => {
    const [message, setMessage] = useState('');
    const messageMutation = useMessageCreate();
    const { remainingCharacters, limitReached } = useCharacterLimit({
        characterLength: message.length,
        characterLimit: maxLength,
    });
    const inputRef = useRef(null);

    const inputInvalid = (limitReached && remainingCharacters < 0) || message.length === 0;

    const sendMessage = async (event) => {
        event.preventDefault();

        if (!inputInvalid) {
            messageMutation.mutate({ offerId, message });
            setMessage('');
            inputRef.current?.focus();
        }
    };

    const handleChange = (event) => {
        setMessage(event.target.value);
    };

    const handleKeySubmit = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            sendMessage(event);
        }
    };

    return (
        <Box display="flex" borderTopWidth="1px" borderTopStyle="solid" borderTopColor="border.muted">
            <Box width="85%">
                <form onSubmit={sendMessage}>
                    <GrowingTextArea
                        ref={inputRef}
                        onChange={handleChange}
                        value={message}
                        placeholder={translate('placeholder.write')}
                        onKeyDown={handleKeySubmit}
                    />
                </form>
            </Box>
            <SendButtonWrapper>
                {remainingCharacters < 50 ? (
                    <Box marginBottom="8px">
                        <CharacterCount count={message.length} limit={maxLength} />
                    </Box>
                ) : null}
                <ChatSendButton Icon={IconArrowUp} disabled={inputInvalid} emphasis="medium" onClick={sendMessage} />
            </SendButtonWrapper>
        </Box>
    );
};
